import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Stop,
  Download,
  Error,
  CheckCircle,
  Cancel,
  Refresh,
  Analytics,
  SaveAlt,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { ProcessingJob, JobStatus } from '../types';
import AudioPlayer from './AudioPlayer';
import AudioMeter from './AudioMeter';
import LoudnessMeters from './LoudnessMeters';
import FrequencyResponseChart from './FrequencyResponseChart';

interface ProcessingInterfaceProps {
  job: ProcessingJob | null;
  onCancel: () => void;
  onDownload: (fileId: string, filename: string) => void;
  onClear: () => void;
  onRunAnalysis: () => void;
}

const getStatusColor = (status: JobStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'idle': return 'default';
    case 'uploading': return 'info';
    case 'processing': return 'primary';
    case 'completed': return 'success';
    case 'error': return 'error';
    case 'cancelled': return 'warning';
    default: return 'default';
  }
};

const getStatusIcon = (status: JobStatus) => {
  switch (status) {
    case 'completed': return <CheckCircle />;
    case 'error': return <Error />;
    case 'cancelled': return <Cancel />;
    default: return null;
  }
};

const ProcessingInterface: React.FC<ProcessingInterfaceProps> = ({
  job,
  onCancel,
  onDownload,
  onClear,
  onRunAnalysis,
}) => {
  // State for audio meter
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [audioVolume, setAudioVolume] = useState<number>(1);

  // State for button loading
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [isExportingAnalysis, setIsExportingAnalysis] = useState(false);

  // Memoize callbacks to prevent AudioPlayer useEffect from running on every render
  const handleAudioReady = useCallback((element: HTMLAudioElement, context: AudioContext, volume: number) => {
    setAudioElement(element);
    setAudioContext(context);
    setAudioVolume(volume);
  }, []);

  const handleVolumeChange = useCallback((volume: number) => {
    setAudioVolume(volume);
  }, []);

  // Handle download with loading state
  const handleDownload = useCallback(async (fileId: string, filename: string) => {
    setDownloadingFileId(fileId);
    try {
      await onDownload(fileId, filename);
    } finally {
      // Keep showing loading for a moment to give user feedback
      setTimeout(() => setDownloadingFileId(null), 1000);
    }
  }, [onDownload]);

  // Handle analysis with loading state
  const handleRunAnalysis = useCallback(async () => {
    setIsAnalysisRunning(true);
    try {
      await onRunAnalysis();
    } finally {
      // Keep showing loading for a moment
      setTimeout(() => setIsAnalysisRunning(false), 1000);
    }
  }, [onRunAnalysis]);

  // Export combined analysis report (LUFS + Frequency)
  const handleExportAnalysis = useCallback(async () => {
    setIsExportingAnalysis(true);
    try {
      const analysisSection = document.getElementById('analysis-report-section');
      if (!analysisSection) {
        alert('Analysis section not found. Please ensure analysis has been run.');
        setIsExportingAnalysis(false);
        return;
      }

      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(analysisSection, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
      });

      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          const link = document.createElement('a');
          // Use target filename for the report name
          const targetName = job?.targetFile?.name?.replace(/\.[^/.]+$/, '') || 'mastering';
          link.download = `${targetName}_analysis-report.png`;
          link.href = URL.createObjectURL(blob);
          link.click();
          URL.revokeObjectURL(link.href);
        }
        // Keep loading state for a moment after download
        setTimeout(() => setIsExportingAnalysis(false), 1000);
      });
    } catch (error) {
      console.error('Error exporting analysis:', error);
      alert('Failed to export analysis. Please try again.');
      setIsExportingAnalysis(false);
    }
  }, []);

  // Don't render anything if there's no job
  if (!job) return null;

  const isProcessing = ['uploading', 'processing'].includes(job.status);
  const isCompleted = job.status === 'completed';
  const hasError = job.status === 'error';
  const isCancelled = job.status === 'cancelled';

  const getActiveStep = () => {
    switch (job.status) {
      case 'uploading': return 0;
      case 'processing': return 1;
      case 'completed': return 2;
      case 'error': return job.progress > 0 ? 1 : 0;
      case 'cancelled': return job.progress > 0 ? 1 : 0;
      default: return -1;
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const getProcessingDuration = () => {
    if (!job.createdAt) return null;
    const endTime = job.completedAt || new Date();
    const duration = Math.round((endTime.getTime() - job.createdAt.getTime()) / 1000);
    return `${duration}s`;
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Processing Status
      </Typography>

      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Job Status Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                color={getStatusColor(job.status)}
                icon={getStatusIcon(job.status) || undefined}
              />
              <Typography variant="body2" color="text.secondary">
                Started: {formatTime(job.createdAt)}
              </Typography>
              {job.completedAt && (
                <Typography variant="body2" color="text.secondary">
                  • Duration: {getProcessingDuration()}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              {isProcessing && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Stop />}
                  onClick={onCancel}
                  size="small"
                >
                  Cancel
                </Button>
              )}
              {(isCompleted || hasError || isCancelled) && (
                <IconButton onClick={onClear} size="small" title="Clear job">
                  <Refresh />
                </IconButton>
              )}
            </Stack>
          </Stack>

          {/* Progress Steps */}
          <Stepper activeStep={getActiveStep()} orientation="vertical">
            <Step>
              <StepLabel>Uploading Files</StepLabel>
              <StepContent>
                <Typography variant="body2">
                  Uploading target and reference audio files...
                </Typography>
                {job.status === 'uploading' && (
                  <LinearProgress
                    variant="determinate"
                    value={job.progress}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Processing</StepLabel>
              <StepContent>
                <Typography variant="body2">
                  {job.message || 'Matching and mastering your audio...'}
                </Typography>
                {job.status === 'processing' && (
                  <LinearProgress
                    variant={job.progress > 0 ? 'determinate' : 'indeterminate'}
                    value={job.progress}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Complete</StepLabel>
              <StepContent>
                <Typography variant="body2">
                  Processing completed successfully!
                </Typography>
              </StepContent>
            </Step>
          </Stepper>

          {/* Error Display */}
          {hasError && job.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Processing Failed</Typography>
              <Typography variant="body2">{job.error}</Typography>
            </Alert>
          )}

          {/* Cancelled Display */}
          {isCancelled && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">Processing was cancelled</Typography>
            </Alert>
          )}

          {/* Results & Analysis */}
          {isCompleted && job.resultFiles && job.resultFiles.length > 0 && (
            <Box sx={{
              mt: 3,
              p: 2.5,
              border: '2px solid',
              borderColor: 'info.light',
              borderRadius: 2,
              bgcolor: 'rgba(2, 136, 209, 0.08)'
            }}>
              <Typography variant="h6" gutterBottom sx={{
                fontWeight: 600,
                color: 'info.dark',
                mb: 2
              }}>
                Results & Analysis
              </Typography>

              <Stack spacing={2}>
                {job.resultFiles.map((file) => (
                  <Paper
                    key={file.id}
                    variant="outlined"
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2,
                      mb: 2
                    }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                          {file.filename}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {file.format} • {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                          variant="contained"
                          startIcon={downloadingFileId === file.id ? <CircularProgress size={16} color="inherit" /> : <Download />}
                          onClick={() => handleDownload(file.id, file.filename)}
                          size="small"
                          disabled={downloadingFileId === file.id}
                          sx={{ width: { xs: '50%', sm: 'auto' } }}
                        >
                          {downloadingFileId === file.id ? 'Downloading...' : 'Download'}
                        </Button>
                        {!file.loudnessMetrics && !file.frequencySpectrum && (
                          <Button
                            variant="outlined"
                            startIcon={isAnalysisRunning ? <CircularProgress size={16} color="inherit" /> : <Analytics />}
                            onClick={handleRunAnalysis}
                            size="small"
                            disabled={isAnalysisRunning}
                            sx={{ width: { xs: '50%', sm: 'auto' } }}
                          >
                            {isAnalysisRunning ? 'Analyzing...' : 'Run Analysis'}
                          </Button>
                        )}
                      </Stack>
                    </Box>

                    {/* Audio Player and Meter Side-by-Side */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
                      <AudioPlayer
                        audioUrl={file.downloadUrl}
                        title={`Preview: ${file.filename}`}
                        onAudioReady={handleAudioReady}
                        onVolumeChange={handleVolumeChange}
                      />
                      <AudioMeter
                        audioElement={audioElement}
                        audioContext={audioContext || undefined}
                        volume={audioVolume}
                      />
                    </Box>

                    {/* Analysis Report Section - Wrapped for export */}
                    {(file.loudnessMetrics || file.frequencySpectrum) && (
                      <>
                        <Box id="analysis-report-section" sx={{ mt: 2 }}>
                          {/* Report Header with Filename */}
                          <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                component="img"
                                src="/apple-touch-icon.png"
                                alt="US Mastering"
                                sx={{ width: 32, height: 32 }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                US Mastering Analysis Report
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Target: {job?.targetFile?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Generated: {new Date().toLocaleString()}
                            </Typography>
                          </Box>

                          {/* Loudness Metrics */}
                          {file.loudnessMetrics && (
                            <Box sx={{ mb: file.frequencySpectrum ? 2 : 0 }}>
                              <LoudnessMeters
                                target={file.loudnessMetrics.target}
                                reference={file.loudnessMetrics.reference}
                                mastered={file.loudnessMetrics.mastered}
                              />
                            </Box>
                          )}

                          {/* Frequency Response Chart */}
                          {file.frequencySpectrum && (
                            <Box>
                              <FrequencyResponseChart
                                target={file.frequencySpectrum.target}
                                reference={file.frequencySpectrum.reference}
                                mastered={file.frequencySpectrum.mastered}
                                masteredFilename={file.filename}
                              />
                            </Box>
                          )}
                        </Box>

                        {/* Export Button at Bottom */}
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={isExportingAnalysis ? <CircularProgress size={20} color="inherit" /> : <SaveAlt />}
                            onClick={handleExportAnalysis}
                            disabled={isExportingAnalysis}
                            sx={{ minWidth: 200 }}
                          >
                            {isExportingAnalysis ? 'Generating Report...' : 'Export Analysis Report'}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {/* Job Details */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Target: {job.targetFile.name} • Reference: {job.referenceFile.name}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              Format: {job.config.outputFormat.toUpperCase()} •
              Limiter: {job.config.useLimiter !== false ? 'On' : 'Off'} •
              Normalize: {job.config.normalize !== false ? 'On' : 'Off'}
            </Typography>
          </Box>
        </Paper>
    </Box>
  );
};

export default ProcessingInterface;